import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import Layout from '../components/layout/AppLayout';
import Header from '../../shared/components/layout/Header';
import Loader from '../../shared/components/ui/Loader';
import { useSidebar } from '../../shared/contexts/SidebarContext';
import { useProjectsList } from '../../../contexts/ProjectsContext';
import { getManagementTasks } from '../services/api/implementation/impl';
import FormInput from '../../shared/components/ui/FormInput';
import { useForm } from 'react-hook-form';
import UserAvatar from '../../shared/components/ui/UserAvatar';
import TaskBadges from './Management/TaskBadges';
import TaskProgress from './Management/TaskProgress';
import TaskCard, { Chip } from './Management/TaskCard';
import CardGridEmpty from './Management/CardGridEmpty';
import { statusChipClass } from './Management/taskWorkflow';
import { displayRootStatus } from './Management/taskTree';
import { formatRupees } from './Management/budget';
import { kindChipTone, kindLabel } from './Management/permission';

const OperationsManagementTaskPage = ({
    embedded = false,
    detailBasePath
}) => {
    const { collapsed } = useSidebar();
    const navigate = useNavigate();
    const location = useLocation();
    const { projectsForDropdown } = useProjectsList();
    const [projectId, setProjectId] = useState(null);
    const [tasks, setTasks] = useState([]);
    const [overdueOnly, setOverdueOnly] = useState(false);

    const { register, watch, setValue } = useForm();
    const selectedProject = watch('project');
    const taskDetailBase = (detailBasePath || location.pathname).replace(/\/$/, '');

    useEffect(() => {
        if (projectsForDropdown?.length > 0 && !selectedProject) {
            setValue('project', projectsForDropdown[0].id);
            setProjectId(projectsForDropdown[0].id);
        }
    }, [projectsForDropdown, setValue, selectedProject]);

    useEffect(() => {
        if (selectedProject) {
            setProjectId(selectedProject);
        }
    }, [selectedProject]);

    const { data: tasksData, isLoading } = useQuery({
        queryKey: ['managementTasks', 'mine', projectId, overdueOnly],
        queryFn: () => getManagementTasks({
            projectId,
            mine: true,
            limit: 50,
            overdue: overdueOnly || undefined
        }),
        enabled: !!projectId,
    });

    useEffect(() => {
        if (tasksData?.data) {
            setTasks(Array.isArray(tasksData.data) ? tasksData.data : []);
        }
    }, [tasksData]);

    const formatDate = (dateString) => {
        if (!dateString) return '---';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-GB', {
            day: '2-digit',
            month: 'short',
            year: 'numeric'
        });
    };

    const toolbar = (
        <div className={`flex items-end gap-4 ${embedded ? 'bg-white px-4 md:px-6 py-3 border-b' : ''}`}>
            <div className="w-full md:w-[18rem]">
                <FormInput
                    width={100}
                    label={'Project'}
                    register={register}
                    register_key={'project'}
                    type={'dropdown'}
                    options={projectsForDropdown}
                />
            </div>
            <div className="flex h-10 shrink-0 items-center gap-1 rounded-lg border border-gray-200 p-1">
                <button
                    type="button"
                    onClick={() => setOverdueOnly(false)}
                    className={`px-3 py-1.5 text-sm rounded ${!overdueOnly ? 'bg-[#346C75] text-white' : 'text-gray-600 hover:bg-gray-50'}`}
                >
                    All
                </button>
                <button
                    type="button"
                    onClick={() => setOverdueOnly(true)}
                    className={`px-3 py-1.5 text-sm rounded ${overdueOnly ? 'bg-[#346C75] text-white' : 'text-gray-600 hover:bg-gray-50'}`}
                >
                    Overdue
                </button>
            </div>
        </div>
    );

    const priorityTone = (priority) =>
        priority === 'High' ? 'rose' : priority === 'Medium' ? 'amber' : 'positive';

    const grid = (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 pb-4">
            {tasks && tasks.length > 0 ? (
                tasks.map((task) => (
                    <TaskCard
                        key={task.id}
                        title={task.title}
                        onOpen={() => navigate(`${taskDetailBase}/${task.id}`)}
                        subtitle={
                            <span
                                className={`mt-1 inline-flex items-center rounded-full px-2.5 py-1 text-[12px] font-semibold ${statusChipClass(displayRootStatus(task))}`}>
                                {displayRootStatus(task) || '---'}
                            </span>
                        }
                    >
                        <div className="flex items-center gap-2 text-base text-[#4B5563] min-w-0">
                            <UserAvatar name={task.owner?.name} size={28} />
                            <span className="truncate">{task.owner?.name || '---'}</span>
                        </div>

                        <TaskProgress progress={task.progress} />

                        <div className="flex items-center gap-2 flex-wrap">
                            <Chip tone={kindChipTone(task.kind)}>{kindLabel(task.kind)}</Chip>
                            <Chip tone={priorityTone(task.priority)}>{task.priority || '---'}</Chip>
                            <Chip tone="positive">Budget: {formatRupees(task.budget)}</Chip>
                            <Chip tone="indigo">Due: {formatDate(task.dueDate)}</Chip>
                        </div>

                        <TaskBadges badges={task.badges} className="flex flex-wrap gap-1" />
                    </TaskCard>
                ))
            ) : (
                <CardGridEmpty message="No tasks found" />
            )}
        </div>
    );

    if (embedded) {
        return (
            <>
                <Loader isVisible={isLoading} />
                {toolbar}
                <div className="px-4 md:px-6 py-4">
                    {grid}
                </div>
            </>
        );
    }

    return (
        <>
            <Loader isVisible={isLoading} />
            <Layout>
                <div
                    className={`fixed z-[10] top-0 transition-all duration-300 ease-in-out ${collapsed ? 'left-[76px] right-0' : 'left-64 right-0'
                        }`}>
                    <Header backArrow={false} data={{ name: 'Management Tasks' }} />
                </div>

                <div className="mt-[6rem] px-6">
                    <div className={`fixed top-[4.5rem] z-30 bg-white px-6 py-3 transition-all duration-300 ease-in-out border-b ${collapsed ? 'left-[76px] right-0' : 'left-64 right-0'
                        }`}>
                        {toolbar}
                    </div>

                    <div className="pt-[5rem]">
                        {grid}
                    </div>
                </div>
            </Layout>
        </>
    );
};

export default OperationsManagementTaskPage;
